import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Modal,
  FormLayout,
  TextField,
  EmptyState,
  Spinner,
  Banner,
  Autocomplete,
  ButtonGroup,
  Text
} from '@shopify/polaris';
import Toast from '../components/Toast';
import { DeleteMajor } from '@shopify/polaris-icons';
import { api } from '../utils/api';
import { variantOptionsFor, loadAllVariants } from '../utils/variants';
import { savedMessage } from '../utils/checkoutSync';
import TagPicker, { displayTag, useTagSuggestions } from '../components/TagPicker';
import VariantSelect from '../components/VariantSelect';

// Centre a DataTable header or cell. Polaris DataTable has no per-column centre
// option (only left/right via columnContentTypes), so wrap the content. Flex
// (not just text-align) so it also centres non-text cells like the limit input.
const center = (content) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{content}</div>
);

function Products() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limits, setLimits] = useState([]);
  const [filteredLimits, setFilteredLimits] = useState([]);
  const [modalActive, setModalActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [limitToDelete, setLimitToDelete] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [queryValue, setQueryValue] = useState('');
  // Tag limits are switched on per shop; without the switch the page is unchanged.
  const productTags = useTagSuggestions('product');
  const [targetType, setTargetType] = useState('product');
  const [tag, setTag] = useState('');
  // Unsaved edits to rows in the list, by limit id: { limitQuantity, variantId }.
  // A row's Save button shows while its draft differs from what's saved.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  // A dialog's button shows a spinner while its save or removal runs, so the
  // pause before it closes doesn't look like nothing happened.
  const [dialogBusy, setDialogBusy] = useState(false);
  const whileBusy = (action) => async () => {
    setDialogBusy(true);
    try {
      await action();
    } finally {
      setDialogBusy(false);
    }
  };

  // Product search state
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantOptions, setVariantOptions] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [skipNextSearch, setSkipNextSearch] = useState(false);
  const [productSearchEmpty, setProductSearchEmpty] = useState(false);
  // Tracks whether the Shopify search path has been warmed this page session, so
  // the warm-up request fires only once.
  const searchWarmedRef = useRef(false);
  // The product last picked in the add dialog, so its full variant list isn't
  // overwritten by a slower load for a product picked before it.
  const pickedProductRef = useRef(null);

  // Form state for adding new limits
  const [formData, setFormData] = useState({
    productId: '',
    productTitle: '',
    variantId: '',
    variantTitle: '',
    limitQuantity: '1'
  });

  useEffect(() => {
    loadLimits();
  }, []);

  // Re-filter whenever the loaded limits or the search query change.
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limits, queryValue]);

  // Debounced product search
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProductOptions([]);
      setProductSearchEmpty(false);
      return;
    }
    try {
      setProductSearchLoading(true);
      setProductSearchEmpty(false);
      const response = await api.get(`/products/search/shopify?query=${encodeURIComponent(query)}`);
      const options = response.data.map(product => ({
        value: product.id,
        label: product.title,
        product: product
      }));
      setProductOptions(options);
      setProductSearchEmpty(options.length === 0);
    } catch (err) {
      console.error('Product search error:', err);
      setProductOptions([]);
      setProductSearchEmpty(true);
    } finally {
      setProductSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip search if flag is set (value was set programmatically)
    if (skipNextSearch) {
      setSkipNextSearch(false);
      return;
    }
    const timer = setTimeout(() => {
      searchProducts(productSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchQuery, searchProducts, skipNextSearch]);

  const handleProductSelect = useCallback((selected) => {
    if (selected.length > 0) {
      const option = productOptions.find(opt => opt.value === selected[0]);
      if (option) {
        setSelectedProduct(option);
        setSkipNextSearch(true); // Don't trigger search when setting the value
        setProductSearchQuery(option.label); // Show selected product in search box

        // Show the variants the search returned straight away, then every variant
        // once loaded (search results only carry a product's first few). A later
        // pick of another product wins if the lists arrive out of order.
        pickedProductRef.current = option.product.id;
        setVariantOptions(variantOptionsFor(option.product.variants));
        loadAllVariants(option.product)
          .then((variants) => {
            if (pickedProductRef.current === option.product.id) {
              setVariantOptions(variantOptionsFor(variants));
            }
          })
          .catch((err) => {
            console.error('Load variants error:', err);
            setError('Failed to load all of this product\'s variants. Please pick the product again.');
          });
        setSelectedVariant(null);

        setFormData(prev => ({
          ...prev,
          productId: option.product.id,
          productTitle: option.product.title,
          variantId: '',
          variantTitle: ''
        }));
      }
    } else {
      setSelectedProduct(null);
      setVariantOptions([]);
      setSelectedVariant(null);
    }
  }, [productOptions]);

  const handleVariantSelect = useCallback((selected) => {
    if (selected.length > 0) {
      const option = variantOptions.find(opt => opt.value === selected[0]);
      if (option) {
        setSelectedVariant(option);
        setFormData(prev => ({
          ...prev,
          variantId: option.value,
          variantTitle: option.label !== 'All Variants' ? option.label : ''
        }));
      }
    } else {
      setSelectedVariant(null);
      setFormData(prev => ({
        ...prev,
        variantId: '',
        variantTitle: ''
      }));
    }
  }, [variantOptions]);

  const loadLimits = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/products');
      setLimits(response.data);
      setDrafts({});
    } catch (err) {
      console.error('Load limits error:', err);
      setError('Failed to load product limits');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...limits];

    // Apply search query
    if (queryValue) {
      filtered = filtered.filter(limit =>
        (limit.productTitle || limit.tag || '').toLowerCase().includes(queryValue.toLowerCase()) ||
        (limit.variantTitle && limit.variantTitle.toLowerCase().includes(queryValue.toLowerCase()))
      );
    }

    setFilteredLimits(filtered);
  };

  const draftFor = (limit) => ({
    limitQuantity: String(limit.limitQuantity),
    variantId: limit.variantId || '',
    ...drafts[limit._id]
  });

  const updateDraft = (limit, change) => {
    setDrafts(prev => ({ ...prev, [limit._id]: { ...draftFor(limit), ...change } }));
  };

  const isDirty = (limit) => {
    const draft = draftFor(limit);
    return parseInt(draft.limitQuantity, 10) !== limit.limitQuantity ||
      draft.variantId !== (limit.variantId || '');
  };

  // Save a row's edited limit and, for a product limit, the variant it covers.
  const handleRowSave = async (limit) => {
    const draft = draftFor(limit);
    const quantity = parseInt(draft.limitQuantity, 10);
    if (!Number.isFinite(quantity) || quantity < 0) {
      setError('Enter a limit of 0 or more.');
      return;
    }

    setSavingId(limit._id);
    try {
      let response;
      let variantTitle = limit.variantTitle || '';
      if (limit.targetType === 'tag') {
        response = await api.put(`/products/tag-limits/${limit._id}`, { limitQuantity: quantity });
      } else {
        const variant = (limit.productVariants || []).find(v => v.id === draft.variantId);
        variantTitle = draft.variantId ? (variant ? variant.title : variantTitle) : '';
        response = await api.put(`/products/limit/${limit._id}`, {
          limitQuantity: quantity,
          variantId: draft.variantId,
          variantTitle
        });
      }

      setLimits(prev => prev.map(l =>
        l._id === limit._id
          ? { ...l, limitQuantity: quantity, variantId: draft.variantId, variantTitle }
          : l
      ));
      setDrafts(prev => {
        const next = { ...prev };
        delete next[limit._id];
        return next;
      });
      showToast(savedMessage(response, 'Limit updated successfully'));
    } catch (err) {
      console.error('Update limit error:', err);
      setError(err.status === 409 ? err.message : 'Failed to update limit. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  // The variant a product limit covers, as a dropdown when the product has
  // variants; otherwise its title, "All variants", or "-" when there's nothing to pick.
  const variantCell = (limit) => {
    if (limit.targetType === 'tag') return '-';
    const variants = limit.productVariants || [];
    if (variants.length === 0) {
      return limit.variantTitle || (limit.productHasVariants ? 'All variants' : '-');
    }
    const draft = draftFor(limit);
    const options = [
      { label: 'All variants', value: '' },
      ...variants.map(v => ({ label: v.title, value: v.id }))
    ];
    // A variant since removed from the product still shows as the current choice.
    if (draft.variantId && !variants.some(v => v.id === draft.variantId)) {
      options.push({ label: limit.variantTitle || 'Removed variant', value: draft.variantId });
    }
    return (
      <div style={{ minWidth: '160px' }}>
        <VariantSelect
          options={options}
          value={draft.variantId}
          onChange={(value) => updateDraft(limit, { variantId: value })}
        />
      </div>
    );
  };

  // Opening the confirmation modal replaces the native window.confirm, whose heading
  // ("An embedded page at … says") is fixed by the browser and can't be branded.
  const handleDelete = (limit) => {
    setLimitToDelete(limit);
    setDeleteModalActive(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalActive(false);
    setLimitToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    const limit = limitToDelete;
    if (!limit) return;

    // The dialog stays open, its Remove button spinning, until the removal is done.
    try {
      let response;
      if (limit.targetType === 'tag') {
        response = await api.delete(`/products/tag-limits/${limit._id}`);
      } else {
        // URL encode the productId to handle Shopify GID format (gid://shopify/Product/123)
        const encodedProductId = encodeURIComponent(limit.productId);
        response = await api.delete(`/products/${encodedProductId}${limit.variantId ? `?variantId=${encodeURIComponent(limit.variantId)}` : ''}`);
      }
      setLimits(prev => prev.filter(l => l._id !== limit._id));
      showToast(savedMessage(response, 'Product limit removed successfully'));
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to remove product limit');
    } finally {
      setDeleteModalActive(false);
      setLimitToDelete(null);
    }
  };

  const handleModalOpen = () => {
    setFormData({
      productId: '',
      productTitle: '',
      variantId: '',
      variantTitle: '',
      limitQuantity: '1'
    });
    setProductSearchQuery('');
    setSelectedProduct(null);
    setProductOptions([]);
    setProductSearchEmpty(false);
    setVariantOptions([]);
    setSelectedVariant(null);
    setTargetType('product');
    setTag('');
    setModalActive(true);

    // The first Shopify product search of a session pays a one-time cost (loading
    // shop settings + validating the access token before the query even runs),
    // which makes the dropdown feel dead on the first keystroke. Fire a throwaway
    // search now, while the user is still reaching for the keyboard, so their real
    // query hits an already-warm path and results appear immediately.
    if (!searchWarmedRef.current) {
      searchWarmedRef.current = true;
      api.get('/products/search/shopify?query=__warmup__').catch(() => {});
    }
  };

  const handleModalClose = () => {
    setModalActive(false);
  };

  const handleSave = async () => {
    try {
      let response;
      if (targetType === 'tag') {
        response = await api.post('/products/tag-limits', { tag, limitQuantity: parseInt(formData.limitQuantity) });
      } else {
        const payload = {
          ...formData,
          limitQuantity: parseInt(formData.limitQuantity),
          isActive: true
        };

        response = await api.post(`/products/${formData.productId}`, payload);
      }
      showToast(savedMessage(response, 'Product limit created successfully'));
      handleModalClose();
      loadLimits();
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save product limit');
    }
  };

  const showToast = (message) => {
    setToastMessage(message);
    setToastActive(true);
  };

  if (loading) {
    return (
      <Page fullWidth title="Product Limits">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const tableRows = filteredLimits.map((limit) => {
    const dirty = isDirty(limit);
    return [
      limit.targetType === 'tag'
        ? <Text as="span">Tag: <Text as="span" fontWeight="semibold">{displayTag(limit.tag, productTags)}</Text></Text>
        : (
          <div>
            <Text as="span">{limit.productTitle}</Text>
            {typeof limit.blockedCount === 'number' && (
              <Text as="p" variant="bodySm" color="subdued">Orders blocked: {limit.blockedCount}</Text>
            )}
          </div>
        ),
      center(variantCell(limit)),
      center(
        <div style={{ width: '80px' }}>
          <TextField
            label="Limit"
            labelHidden
            type="number"
            value={draftFor(limit).limitQuantity}
            onChange={(value) => updateDraft(limit, { limitQuantity: value })}
            min="0"
            autoComplete="off"
          />
        </div>
      ),
      // Remove, then Save at the far right. Save's space is always kept (it's only
      // hidden until something changes), so nothing in the row moves when it appears.
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
        <Button
          plain
          icon={DeleteMajor}
          onClick={() => handleDelete(limit)}
          accessibilityLabel="Remove limit"
        />
        <div style={{ visibility: dirty ? 'visible' : 'hidden' }} aria-hidden={!dirty}>
          <Button
            size="slim"
            primary
            loading={savingId === limit._id}
            disabled={!dirty}
            onClick={() => handleRowSave(limit)}
          >
            Save
          </Button>
        </div>
      </div>
    ];
  });

  return (
    <Page
      fullWidth
      title="Product Limits"
      subtitle="Manage purchase limits for your products"
      primaryAction={{
        content: 'Add Limit',
        onAction: handleModalOpen
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner status="critical" title="Error">
              <p>{error}</p>
              <Button onClick={loadLimits}>Retry</Button>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <TextField
                placeholder="Search products..."
                value={queryValue}
                onChange={setQueryValue}
                clearButton
                onClearButtonClick={() => setQueryValue('')}
              />
            </div>

            {filteredLimits.length > 0 ? (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={['Product/Tag', center('Variant'), center('Limit'), '']}
                rows={tableRows}
              />
            ) : (
              <EmptyState
                heading={limits.length === 0 ? "No product limits configured" : "No limits match your search"}
                action={{
                  content: 'Add Product Limit',
                  onAction: handleModalOpen
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  {limits.length === 0
                    ? "Set purchase limits on your products to control customer purchases."
                    : "Try adjusting your search criteria."
                  }
                </p>
              </EmptyState>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add Modal */}
      <Modal
        open={modalActive}
        onClose={handleModalClose}
        title="Add Product Limit"
        primaryAction={{
          content: 'Save',
          onAction: whileBusy(handleSave),
          loading: dialogBusy,
          disabled: (targetType === 'tag' ? !tag : (!formData.productId || !formData.productTitle)) || !formData.limitQuantity
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleModalClose
          }
        ]}
      >
        <Modal.Section>
          {/* Every field is a direct child of FormLayout so each gets the same
              spacing (a fragment would be treated as one row with no inner gaps). */}
          <FormLayout>
            <ButtonGroup segmented>
              <Button primary={targetType === 'product'} onClick={() => setTargetType('product')}>
                Product
              </Button>
              <Button primary={targetType === 'tag'} onClick={() => setTargetType('tag')}>
                Product tag
              </Button>
            </ButtonGroup>

            {targetType === 'tag' && (
              <TagPicker
                kind="product"
                label="Product tag"
                value={tag}
                onChange={setTag}
              />
            )}

            {targetType !== 'tag' && (
              <div>
                <Autocomplete
                  options={productOptions}
                  selected={selectedProduct ? [selectedProduct.value] : []}
                  onSelect={handleProductSelect}
                  loading={productSearchLoading}
                  emptyState={productSearchEmpty ? (
                    <div style={{ padding: '12px 16px', color: '#6b7280' }}>
                      No products found
                    </div>
                  ) : null}
                  textField={
                    <Autocomplete.TextField
                      label="Product"
                      value={productSearchQuery}
                      onChange={(value) => {
                        setProductSearchQuery(value);
                        // Flip on the loading state synchronously with the keystroke.
                        // Polaris only opens the options popover on a keystroke while
                        // the ComboBox already has a child (a loading row counts), so
                        // without this the async results arrive with the popover shut
                        // and don't appear until the next keystroke. Mirrors the fix in
                        // Customers.js (commit c49322b).
                        setProductSearchLoading(value.trim().length >= 2);
                      }}
                      placeholder="Start typing to search products..."
                      autoComplete="off"
                    />
                  }
                />

                {selectedProduct && (
                  <Text as="p" color="subdued">
                    Selected: {selectedProduct.label}
                  </Text>
                )}
              </div>
            )}

            {targetType !== 'tag' && selectedProduct && variantOptions.length > 0 && (
              <Autocomplete
                options={variantOptions}
                selected={selectedVariant ? [selectedVariant.value] : []}
                onSelect={handleVariantSelect}
                textField={
                  <Autocomplete.TextField
                    label="Variant"
                    value={selectedVariant ? selectedVariant.label : ''}
                    onChange={() => {}}
                    placeholder="Select a variant or choose 'All Variants'"
                    autoComplete="off"
                  />
                }
              />
            )}

            <TextField
              label="Limit Quantity"
              type="number"
              value={formData.limitQuantity}
              onChange={(value) => setFormData({ ...formData, limitQuantity: value })}
              placeholder="Maximum quantity allowed"
              min="0"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Remove Limit confirmation */}
      <Modal
        open={deleteModalActive}
        onClose={handleDeleteCancel}
        title="Remove product limit"
        primaryAction={{
          content: 'Remove',
          destructive: true,
          onAction: whileBusy(handleDeleteConfirm),
          loading: dialogBusy
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleDeleteCancel
          }
        ]}
      >
        <Modal.Section>
          <div className="og-confirm-modal">
            <Text as="p">
              Are you sure?
            </Text>
          </div>
        </Modal.Section>
      </Modal>

      {toastActive && (
        <Toast
          content={toastMessage}
          onDismiss={() => setToastActive(false)}
        />
      )}
    </Page>
  );
}

export default Products;
